import classnames from 'classnames';
import * as React from 'react';
import { Ref } from 'react-hook-form';

import './index.scss';

interface IRefs {
    root?: (ref: HTMLInputElement) => void;
}

interface IProps {
    className?: string;

    /** Input autocomplete */
    autoComplete?: string;

    /** Autofocus attribute. */
    autoFocus?: boolean;

    /** Disabled. */
    disabled?: boolean;

    /** Maximum number of characters allowed in the input. */
    maxLength?: number;

    /** The function will be called when the field is focused. */
    onFocus?: (value: string, e?: React.SyntheticEvent<any>) => void;

    /** The function will be called when the field is blured. */
    onBlur?: (value: string, e?: React.SyntheticEvent<any>) => void;

    /** The function will be called when the value in the field is changed. */
    onChange?: (value: string, e?: React.SyntheticEvent<any>) => void;

    /** The function will be called when the value in the field is clicked. */
    onClick?: (value: string, e?: React.SyntheticEvent<any>) => void;

    /** The function will be called when the key is pressed. */
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;

    /** Placeholder. */
    placeholder?: string;

    /** Prefix content */
    prefix?: string;

    /** Postfix content */
    postfix?: string;

    /** Readonly. */
    readonly?: boolean;

    /** Input Value. */
    value?: string | null;

    /** ID. */
    id?: string;

    refs?: IRefs;
    name?: string;
    register: Ref;

    type?: string;
}

export class Input extends React.Component<IProps> {
    private handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { onChange, disabled } = this.props;

        if (disabled) {
            event.preventDefault();
        } else if (onChange) {
            onChange(event.currentTarget.value, event);
        }
    }

    private handleFocus = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { onFocus, disabled } = this.props;

        if (!disabled) {
            this.setState({
                focused: true,
            });

            if (onFocus) {
                onFocus(event.currentTarget.value, event);
            }
        } else {
            event.preventDefault();
        }
    }

    private handleBlur = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { onBlur, disabled } = this.props;

        if (!disabled) {
            this.setState({
                focused: false,
            });

            if (onBlur) {
                onBlur(event.currentTarget.value, event);
            }
        } else {
            event.preventDefault();
        }
    }

    private handleClick = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { onClick, disabled } = this.props;

        if (disabled) {
            event.preventDefault();
        } else if (onClick) {
            onClick(event.currentTarget.value, event);
        }
    }

    /**
     * Handler for keyboard keydown event.
     */
    private handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        const { key, keyCode, shiftKey } = event;
        const keyLower = key.toLowerCase();
        const keyUpper = key.toUpperCase();
        const capsLockPressed = keyCode === 20;
        const letterPressed = key && key.length === 1 && keyLower !== keyUpper;

        if (capsLockPressed) {
            this.setState({ isCapsLockOn: true });
        } else if (letterPressed) {
            const isCapsLockOn = (keyLower === key && shiftKey) || (keyUpper === key && !shiftKey);
            this.setState({ isCapsLockOn });
        }

        if (this.props.onKeyDown) {
            this.props.onKeyDown(event);
        }
    }

    public render (): JSX.Element {
        const {
            placeholder,
            disabled,
            readonly,
            className,
            id,
            register,
            name,
            type = 'text'
        } = this.props;
        const classes = classnames('input-style', className);

        return (
            <div className="input-form">
                <input
                    autoComplete="off"
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readonly}
                    className={classes}
                    ref={register}
                    id={id}
                    onFocus={this.handleFocus}
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                    onClick={this.handleClick}
                    onKeyDown={this.handleKeyDown}
                    name={name}
                    type={type}
                />
            </div>
        );
    }
}
